import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  FormHelperText,
} from "@mui/material";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../Services/AuthService";
import { validateConfirmPassword, validatePassword } from "../utils/validationUtils";
import { isValid } from "date-fns";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    let isValid = true;
    const errs: typeof errors = {};

    const passwordError = validatePassword(password);
    if (passwordError) {
      errs.password = passwordError;
    }

    const confirmError = validateConfirmPassword(
      password,
      confirmPassword
    );
    if (confirmError) {
      errors.confirmPassword = confirmError;
      isValid = false;
    }

    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    try {
      setLoading(true);
      const res = await resetPassword({
        token: token as string,
        newPassword: password,
      });
      Swal.fire("Success", res.message, "success");
      setPassword("");
      setConfirmPassword("");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("orgId");
      localStorage.removeItem("orgName");
      localStorage.clear();
      sessionStorage.clear();
      navigate("/", { replace: true });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Reset failed";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Typography variant="h6" color="error" textAlign="center">
        Invalid or expired reset link.
      </Typography>
    );
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="#f0f2f5"
      px={2}
    >
      <Paper elevation={3} sx={{ maxWidth: 400, width: "100%", p: 4 }}>
        <Typography variant="h5" textAlign="center" fontWeight="bold" mb={2}>
          Reset Password
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!errors.password}
              fullWidth
            />
            {errors.password && (
              <FormHelperText error>{errors.password}</FormHelperText>
            )}

            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!errors.confirmPassword}
              fullWidth
            />
            {errors.confirmPassword && (
              <FormHelperText error>{errors.confirmPassword}</FormHelperText>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
